import React from "react";
import { Box, Typography, Container, Button } from "@mui/material";

const GoogleSearchResultsExportContent = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Container maxWidth="md" sx={{ textAlign: "center", mt: 4 }}>
      {/* Headline Section */}
      <Typography variant="h4" fontWeight="bold" sx={{ fontSize: "1.75rem" }} gutterBottom>
        Export Google Search Results Instantly
      </Typography>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Save, Analyze & Optimize SERP Data in Seconds
      </Typography>

      {/* Problem/Solution */}
      <Typography variant="body1" paragraph>
        Manually checking Google search results is tedious and inefficient. Whether you're an SEO professional, digital marketer, or content strategist — you deserve a better way to extract data.
      </Typography>
      <Typography variant="body1" paragraph sx={{ fontWeight: 'bold', color: 'primary.main' }}>
        Our Google Search Results Export tool captures the top organic results into a structured CSV or Excel format — helping you make data-driven decisions instantly.
      </Typography>

      {/* Benefits */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={3}>
        Why Export Google Search Results?
      </Typography>
      <ul style={{ textAlign: "left", display: "inline-block", listStyleType: 'none', paddingLeft: 0 }}>
        <li>✅ <strong>Save time:</strong> No more copying SERPs manually</li>
        <li>📈 <strong>SEO insights:</strong> Track keyword rankings and domain positions</li>
        <li>🔬 <strong>Competitor tracking:</strong> See who ranks for your target keywords</li>
        <li>📊 <strong>Content planning:</strong> Understand what Google prioritizes for your keywords</li>
        <li>📁 <strong>Multiple formats:</strong> Download results in CSV or Excel for deeper analysis</li>
      </ul>

      {/* How It Works */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
        How to Export Google Search Results in 3 Simple Steps
      </Typography>
      <Box sx={{ 
        backgroundColor: '#f5f5f5', 
        p: 3, 
        borderRadius: 2,
        textAlign: 'left',
        display: 'inline-block'
      }}>
        <ol>
          <li><strong>Enter</strong> your target keyword</li>
          <li><strong>Select</strong> your country for localized results</li>
          <li><strong>Click</strong> "Export Results" to download your SERP data</li>
        </ol>
      </Box>

      {/* Social Proof */}
      <Typography variant="h5" fontWeight="bold" gutterBottom mt={4}>
        Loved by SEO Experts & Agencies
      </Typography>
      <Box sx={{ fontStyle: 'italic', mb: 3 }}>
        <Typography paragraph>"Game-changer for our keyword tracking process. This tool lets us export search rankings for multiple clients in seconds!"</Typography>
        <Typography>- Priya S., SEO Consultant</Typography>
      </Box>

      {/* FAQs */}
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Frequently Asked Questions
      </Typography>

      <Box textAlign="left" sx={{ display: 'inline-block' }}>
        <Typography variant="body1" fontWeight="bold">📌 What data is included in the export?</Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          The export includes page titles, snippets, URLs, domains, and ranks of the top search results.
        </Typography>

        <Typography variant="body1" fontWeight="bold">🌍 Can I get localized results?</Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          Yes! Just choose your country from the dropdown to get country-specific Google results.
        </Typography>

        <Typography variant="body1" fontWeight="bold">⏱️ How fast is the export?</Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          It typically takes less than 10 seconds to generate and download your results.
        </Typography>

        {/* Additional FAQs */}
        <Typography variant="body1" fontWeight="bold">💰 Cheapest and most scalable ways to scrape Google?</Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          Our tool offers an affordable credit-based model and handles Google scraping for you — no proxies, IP rotation, or CAPTCHAs required.
        </Typography>

        <Typography variant="body1" fontWeight="bold">🧩 Easiest way to scrape Google search (first) page?</Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          Simply enter a keyword, choose a country, and export — it's that easy. No coding or setup needed.
        </Typography>

        <Typography variant="body1" fontWeight="bold">⚙️ Automate Google searches and scrape first page results?</Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          With our tool, you can export Google’s first page results instantly and programmatically, making automation easy and scalable.
        </Typography>

        <Typography variant="body1" fontWeight="bold">🔢 How should I scrape 100 Google Search Results?</Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          Our tool retrieves and formats the top results for your keyword — no scraping headaches or result limits on your end.
        </Typography>

        <Typography variant="body1" fontWeight="bold">💸 What's the cheapest service to scrape Google results?</Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          Our platform is designed to be cost-effective and user-friendly, especially compared to proxy or scraper API services.
        </Typography>

        <Typography variant="body1" fontWeight="bold">🔍 How to scrape information from Google?</Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          Use our export tool to extract titles, URLs, domains, snippets, and ranks from Google’s search results — all in one click.
        </Typography>

        <Typography variant="body1" fontWeight="bold">⚡ How to efficiently and reliably scrape Google SERP in real-time?</Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          Our tool ensures real-time data scraping with minimal delay, ideal for timely insights and competitive tracking.
        </Typography>

        <Typography variant="body1" fontWeight="bold">🛠️ Which tools allow you to scrape Google search features?</Typography>
        <Typography variant="body2" paragraph sx={{ mb: 2 }}>
          Our tool focuses on top organic results but is frequently updated to adapt to new SERP features — making it a versatile and reliable choice.
        </Typography>
      </Box>

      {/* CTA Section */}
      <Box mt={4} mb={6}>
        <Typography variant="h6" paragraph sx={{ fontWeight: 'bold' }}>
          Ready to extract Google search results effortlessly?
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          size="large" 
          onClick={scrollToTop}
          sx={{ 
            fontSize: '1.1rem',
            py: 1.5,
            px: 4,
            fontWeight: 'bold'
          }}
        >
          Export My Google Search Results Now →
        </Button>
        <Typography variant="caption" display="block" mt={1}>
          1 credit = 1 full Google search export
        </Typography>
      </Box>
    </Container>
  );
};

export default GoogleSearchResultsExportContent;
